/**
 * ChatPanel - Grounded Q&A interface
 *
 * Uses Gemini AI for grounded responses with citations.
 *
 * @module ChatPanel
 */

import React, { useState, useRef, useEffect } from 'react';

import {
  Send,
  Loader2,
  Copy,
  Check,
  RefreshCw,
  Trash2,
  Sparkles,
  BookOpen,
  AlertTriangle,
  ChevronDown,
  ExternalLink,
  Cloud,
  HardDrive,
  Info,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';

import ideasKitchenApi from '@/pages/ideas-kitchen/api/ideasKitchenApi';
import { FEATURE_FLAGS } from '../../../config/featureFlags';
import { useAuth } from '../../../contexts/AuthContext';
import ChatGreeting from '@/pages/ideas-kitchen/components/ChatGreeting';
import AnimatedResponse from '@/pages/ideas-kitchen/components/AnimatedResponse';
import ProcessingSpinner from '@/pages/ideas-kitchen/components/ProcessingSpinner';

/**
 * Processing Mode Banner - Cloud-First Architecture
 * Shows cloud processing status
 */
const ProcessingModeBanner = ({ notebook }) => {
  const sources = notebook?.sources || [];
  const cloudCount = sources.filter(
    s =>
      s.status === 'ready' &&
      (s.ocrProvider === 'google_vision' ||
        s.extractionProvider === 'google_vision' ||
        s.extractionProvider === 'gemini')
  ).length;
  const fallbackCount = sources.filter(
    s =>
      s.status === 'ready' &&
      ((s.extractionProvider && s.extractionProvider.startsWith('local_')) ||
        s.ocrProvider === 'none')
  ).length;
  const totalCount = sources.length;

  if (totalCount === 0) return null;

  // Cloud-first: Most/all sources are cloud-processed
  if (cloudCount > 0 && fallbackCount === 0) {
    return (
      <div className="flex items-center gap-2 px-4 py-2 bg-emerald-50 dark:bg-emerald-950/30 border-b border-emerald-200 dark:border-emerald-900/50">
        <Cloud className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
        <p className="text-xs text-emerald-700 dark:text-emerald-300 flex-1">
          <strong>Google AI Powered</strong> — {cloudCount} sources processed via Gemini
        </p>
      </div>
    );
  }

  // Fallback mode: Cloud was unavailable
  if (fallbackCount > 0 && cloudCount === 0) {
    return (
      <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-900/50">
        <HardDrive className="h-4 w-4 text-amber-600 dark:text-amber-400" />
        <p className="text-xs text-amber-700 dark:text-amber-300 flex-1">
          <strong>Local Fallback</strong> — Will sync to cloud when available
        </p>
        <div className="flex items-center gap-1 text-[10px] text-amber-600 dark:text-amber-400">
          <Info className="h-3 w-3" />
          {fallbackCount} pending sync
        </div>
      </div>
    );
  }

  // Mixed: Some cloud, some fallback
  return (
    <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-950/30 border-b border-blue-200 dark:border-blue-900/50">
      <Cloud className="h-4 w-4 text-blue-600 dark:text-blue-400" />
      <p className="text-xs text-blue-700 dark:text-blue-300 flex-1">
        <strong>Google AI</strong> — {cloudCount} cloud + {fallbackCount} pending sync
      </p>
    </div>
  );
};

const PROCESSING_HOOKS = {
  queued: 'Getting things ready...',
  uploading: 'Securing your document in the cloud...',
  staged: 'Document received. Preparing for analysis...',
  scanning: 'AI is reading through your document...',
  extracting: 'Pulling out the important details...',
  ocr_processing: 'AI is recognizing every word in your images...',
  chunking: 'Organizing content for intelligent search...',
  importing: 'Connecting your document to Google AI...',
  indexing: 'Google AI is learning your document inside out...',
  search_warming: 'Almost there - warming up search for you...',
};

const PROCESSING_STAGE_SET = new Set(Object.keys(PROCESSING_HOOKS));

const PROCESSING_STEP_MAP = {
  queued: 1,
  uploading: 1,
  staged: 2,
  scanning: 2,
  extracting: 3,
  ocr_processing: 3,
  chunking: 4,
  importing: 5,
  indexing: 6,
  search_warming: 7,
  ready: 8,
  failed: 8,
};

const normalizeStatus = (status) => String(status || '').trim().toLowerCase();

const getStepNumber = (stage) => PROCESSING_STEP_MAP[normalizeStatus(stage)] || 1;

const getFirstName = (user) => {
  const explicit = String(user?.firstName || '').trim();
  if (explicit) return explicit;
  const fullName = String(user?.name || '').trim();
  if (!fullName) return '';
  return fullName.split(/\s+/)[0] || '';
};

const ChatPanel = ({ notebook, onCitationClick }) => {
  const auth = useAuth?.() || {};
  const user = auth?.user || null;
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorTraceId, setErrorTraceId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [animatedAssistantIds, setAnimatedAssistantIds] = useState(() => new Set());
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof document === 'undefined') return false;
    return document.documentElement.classList.contains('dark');
  });

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const canClearChat = FEATURE_FLAGS.IDEAS_KITCHEN_CLEAR_CHAT_ENABLED === true;

  // Load chat history on mount/notebook change
  useEffect(() => {
    if (notebook?.id) {
      loadChatHistory();
    }
  }, [notebook?.id]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return undefined;
    const root = document.documentElement;
    const sync = () => {
      setIsDarkMode(root.classList.contains('dark'));
    };
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const notebookSources = Array.isArray(notebook?.sources) ? notebook.sources : [];
  const hasSources = notebookSources.length > 0;
  const hasReadySource = notebookSources.some(
    source => normalizeStatus(source?.status) === 'ready'
  );
  const processingSources = notebookSources
    .filter(source => PROCESSING_STAGE_SET.has(normalizeStatus(source?.status)))
    .sort((a, b) => getStepNumber(b?.status) - getStepNumber(a?.status));
  const isProcessing = processingSources.length > 0 && !hasReadySource;
  const currentSource = processingSources[0] || null;
  const currentStage = normalizeStatus(currentSource?.status || 'queued');
  const currentStep = getStepNumber(currentStage);
  const currentStageMessage = PROCESSING_HOOKS[currentStage] || 'Preparing your document...';
  const shouldShowProcessingSpinner = messages.length === 0 && hasSources && isProcessing;
  const userFirstName = getFirstName(user);

  const loadChatHistory = async () => {
    try {
      const response = await ideasKitchenApi.getChatHistory(notebook.id);
      if (response.success && response.history) {
        setMessages(response.history);
        setAnimatedAssistantIds(new Set());
      }
    } catch (err) {
      console.error('Failed to load chat history:', err);
    }
  };

  const handleSend = async (overrideInput = null) => {
    const rawInput = typeof overrideInput === 'string' ? overrideInput : input;
    const trimmedInput = String(rawInput || '').trim();
    if (!trimmedInput || !notebook?.id || isLoading) return;

    const clientTraceId =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: trimmedInput,
      traceId: clientTraceId,
      timestamp: new Date().toISOString(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setError(null);
    setErrorTraceId(null);

    try {
      const response = await ideasKitchenApi.askQuestion(notebook.id, trimmedInput, {
        traceId: clientTraceId,
        history: messages.slice(-10), // Last 10 messages for context
      });

      const traceId = response?.traceId || null;
      const answerText = response?.answer?.text || response?.response?.text;
      const answerCitations = response?.answer?.citations || response?.response?.citations || [];
      const answerMeta = response?.answer
        ? {
            confidence: response.answer.confidence,
            inferred: response.answer.inferred,
            taskType: response.answer.taskType,
            structured: response.answer.structured,
          }
        : response?.response?.metadata;

      if (response.success && answerText) {
        const assistantMessage = {
          id: response.chatMessageId || response.response?.id || (Date.now() + 1).toString(),
          role: 'assistant',
          content: answerText,
          citations: answerCitations,
          metadata: answerMeta,
          traceId,
          timestamp: new Date().toISOString(),
        };
        setMessages(prev => [...prev, assistantMessage]);
        setAnimatedAssistantIds(prev => {
          const next = new Set(prev);
          next.add(assistantMessage.id);
          return next;
        });
      } else {
        setError(
          response?.message ||
            response?.error?.message ||
            response?.error ||
            'Failed to get response'
        );
        setErrorTraceId(traceId || clientTraceId);
      }
    } catch (err) {
      console.error('Ask failed:', err);
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.error?.message ||
        err?.error?.code ||
        err?.message ||
        'Failed to get response';
      const trace = err?.response?.data?.traceId || err?.traceId || clientTraceId;
      setError(msg);
      setErrorTraceId(trace);

      // ✅ If we timed out, the backend may still finish (and it saves answers to MongoDB).
      // Auto-refresh history a few times to "catch" the saved assistant reply and clear the error.
      const isTimeout =
        String(msg || '')
          .toLowerCase()
          .includes('timeout') || String(err?.code || '').toUpperCase() === 'ECONNABORTED';
      if (isTimeout) {
        const trySync = async (attempt = 0) => {
          if (!notebook?.id) return;
          if (attempt >= 6) return;
          try {
            const hist = await ideasKitchenApi.getChatHistory(notebook.id);
            if (hist?.success && Array.isArray(hist.history)) {
              const hasAssistant = hist.history.some(
                m => m?.role === 'assistant' && m?.traceId === clientTraceId
              );
              setMessages(hist.history);
              setAnimatedAssistantIds(new Set());
              if (hasAssistant) {
                setError(null);
                setErrorTraceId(null);
                return;
              }
            }
          } catch {
            // ignore
          }
          setTimeout(() => trySync(attempt + 1), 1500 + attempt * 750);
        };
        setTimeout(() => trySync(0), 1200);
      }
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleClearHistory = async () => {
    if (!notebook?.id) return;
    if (!canClearChat) {
      console.warn('[Ideas Kitchen] Clear chat disabled by feature flag.');
      return;
    }

    try {
      await ideasKitchenApi.clearChatHistory(notebook.id);
      setMessages([]);
      setAnimatedAssistantIds(new Set());
    } catch (err) {
      console.error('Failed to clear history:', err);
    }
  };

  const handleCopy = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const renderCitation = (citation, index) => {
    const label =
      citation?.fileName || citation?.sourceName || citation?.sourceId || `Source ${index + 1}`;
    return (
      <button
        key={index}
        onClick={() => onCitationClick?.(citation)}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 text-xs rounded hover:bg-amber-200 dark:hover:bg-amber-900/60 transition-colors"
        title={`View source: ${label}`}
      >
        <BookOpen className="h-3 w-3" />[{index + 1}]
      </button>
    );
  };

  const renderMessageContent = message => {
    const raw = String(message?.content || '');
    const citations = Array.isArray(message?.citations) ? message.citations : [];

    // Convert bracket citations like [1] or [1, 2, 3] into markdown links we can intercept.
    // This preserves markdown richness (headings, lists, bold) while keeping citations clickable.
    const markdown = raw.replace(/\[(\s*\d+(?:\s*,\s*\d+)*\s*)\](?!\()/g, (full, inner) => {
      const nums = String(inner)
        .split(',')
        .map(s => parseInt(s.trim(), 10))
        .filter(n => Number.isFinite(n) && n > 0);

      if (nums.length === 0) return full;

      return nums.map(n => (citations[n - 1] ? `[${n}](ik-cite:${n})` : `[${n}]`)).join(', ');
    });

    return (
      <div className="max-w-none">
        <ReactMarkdown
          components={{
            h1: ({ children, ...props }) => (
              <h1
                className="text-lg font-semibold text-slate-900 dark:text-white mt-3 mb-2"
                {...props}
              >
                {children}
              </h1>
            ),
            h2: ({ children, ...props }) => (
              <h2
                className="text-base font-semibold text-slate-900 dark:text-white mt-3 mb-2"
                {...props}
              >
                {children}
              </h2>
            ),
            h3: ({ children, ...props }) => (
              <h3
                className="text-sm font-semibold text-slate-900 dark:text-white mt-2 mb-1.5"
                {...props}
              >
                {children}
              </h3>
            ),
            p: ({ children, ...props }) => (
              <p className="mb-2 last:mb-0 leading-relaxed" {...props}>
                {children}
              </p>
            ),
            ul: ({ children, ...props }) => (
              <ul className="list-disc pl-5 my-2 space-y-1" {...props}>
                {children}
              </ul>
            ),
            ol: ({ children, ...props }) => (
              <ol className="list-decimal pl-5 my-2 space-y-1" {...props}>
                {children}
              </ol>
            ),
            li: ({ children, ...props }) => (
              <li className="leading-relaxed" {...props}>
                {children}
              </li>
            ),
            strong: ({ children, ...props }) => (
              <strong className="font-semibold text-slate-900 dark:text-white" {...props}>
                {children}
              </strong>
            ),
            em: ({ children, ...props }) => (
              <em className="italic" {...props}>
                {children}
              </em>
            ),
            hr: props => (
              <hr className="my-3 border-slate-200 dark:border-neutral-700" {...props} />
            ),
            blockquote: ({ children, ...props }) => (
              <blockquote
                className="my-2 border-l-2 border-amber-300 dark:border-amber-700 pl-3 text-slate-600 dark:text-neutral-300"
                {...props}
              >
                {children}
              </blockquote>
            ),
            code: ({ inline, children, ...props }) => {
              if (inline) {
                return (
                  <code
                    className="px-1 py-0.5 rounded bg-slate-100 dark:bg-neutral-700 text-slate-800 dark:text-neutral-100 text-[12px]"
                    {...props}
                  >
                    {children}
                  </code>
                );
              }
              return (
                <code className="text-[12px]" {...props}>
                  {children}
                </code>
              );
            },
            pre: ({ children, ...props }) => (
              <pre
                className="my-2 overflow-x-auto rounded-lg bg-slate-50 dark:bg-neutral-900 border border-slate-200 dark:border-neutral-700 p-3 text-[12px]"
                {...props}
              >
                {children}
              </pre>
            ),
            a: ({ href, children, ...props }) => {
              const h = String(href || '');

              // Inline citation: [1](ik-cite:1)
              if (h.startsWith('ik-cite:')) {
                const idx = parseInt(h.replace('ik-cite:', ''), 10) - 1;
                if (Number.isFinite(idx) && idx >= 0 && citations[idx]) {
                  return renderCitation(citations[idx], idx);
                }
                return <span {...props}>{children}</span>;
              }

              // Safety: block non-http(s)/mailto links
              const isSafe =
                h.startsWith('http://') ||
                h.startsWith('https://') ||
                h.startsWith('mailto:') ||
                h.startsWith('#');

              if (!isSafe) {
                return <span {...props}>{children}</span>;
              }

              return (
                <a
                  href={h}
                  target={h.startsWith('http') ? '_blank' : undefined}
                  rel={h.startsWith('http') ? 'noreferrer noopener' : undefined}
                  className="text-amber-700 dark:text-amber-300 underline underline-offset-2 hover:text-amber-800 dark:hover:text-amber-200"
                  {...props}
                >
                  {children}
                </a>
              );
            },
          }}
        >
          {markdown}
        </ReactMarkdown>
      </div>
    );
  };

  // ============================================================
  // ✅ Enterprise: Deterministic rendering for structured answers
  // ============================================================

  const ConfidenceBadge = ({ value }) => {
    const v = String(value || '').toLowerCase();
    const label =
      v === 'high'
        ? 'High'
        : v === 'medium'
          ? 'Medium'
          : v === 'low'
            ? 'Inferred'
            : String(value || 'Inferred');

    const cls =
      v === 'high'
        ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
        : v === 'medium'
          ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200'
          : 'bg-slate-100 dark:bg-neutral-700 text-slate-700 dark:text-neutral-200';

    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${cls}`}
      >
        Confidence: {label}
      </span>
    );
  };

  const ComparisonTable = ({ data, confidence }) => {
    const rows = Array.isArray(data?.comparison) ? data.comparison : [];
    const docNames = Array.from(new Set(rows.flatMap(r => Object.keys(r?.documents || {}))));

    return (
      <div className="space-y-3">
        {data?.overallVerdict && (
          <div className="p-3 rounded-xl bg-slate-50 dark:bg-neutral-900 border border-slate-200 dark:border-neutral-700">
            <div className="flex items-center justify-between gap-2">
              <div className="text-xs font-semibold text-slate-700 dark:text-neutral-200">
                Overall verdict
              </div>
              <ConfidenceBadge value={confidence} />
            </div>
            <div className="mt-1 text-sm text-slate-700 dark:text-neutral-300 whitespace-pre-wrap">
              {data.overallVerdict}
            </div>
          </div>
        )}

        <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-neutral-700 bg-white dark:bg-neutral-800">
          <table className="min-w-[720px] w-full text-xs">
            <thead className="bg-slate-50 dark:bg-neutral-900">
              <tr>
                <th className="text-left px-3 py-2 border-b border-slate-200 dark:border-neutral-700">
                  Dimension
                </th>
                {docNames.map(d => (
                  <th
                    key={d}
                    className="text-left px-3 py-2 border-b border-slate-200 dark:border-neutral-700 min-w-[220px]"
                  >
                    {d}
                  </th>
                ))}
                <th className="text-left px-3 py-2 border-b border-slate-200 dark:border-neutral-700 min-w-[220px]">
                  Verdict
                </th>
                <th className="text-left px-3 py-2 border-b border-slate-200 dark:border-neutral-700 w-[120px]">
                  Row confidence
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={`${row?.dimension || 'row'}-${idx}`} className="align-top">
                  <td className="px-3 py-2 border-b border-slate-100 dark:border-neutral-700 font-semibold text-slate-700 dark:text-neutral-200">
                    {row?.dimension}
                  </td>
                  {docNames.map(d => (
                    <td
                      key={d}
                      className="px-3 py-2 border-b border-slate-100 dark:border-neutral-700 text-slate-700 dark:text-neutral-300 whitespace-pre-wrap"
                    >
                      {row?.documents?.[d] || 'Not specified'}
                    </td>
                  ))}
                  <td className="px-3 py-2 border-b border-slate-100 dark:border-neutral-700 text-slate-700 dark:text-neutral-300 whitespace-pre-wrap">
                    {row?.verdict || 'Not specified'}
                  </td>
                  <td className="px-3 py-2 border-b border-slate-100 dark:border-neutral-700 text-slate-600 dark:text-neutral-400">
                    {String(row?.confidence || '').toUpperCase() || 'INFERRED'}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td
                    className="px-3 py-3 text-slate-500 dark:text-neutral-400"
                    colSpan={docNames.length + 3}
                  >
                    No structured comparison rows returned.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const RisksList = ({ data, confidence, title }) => {
    const items = Array.isArray(data?.risks) ? data.risks : [];

    const severityBadge = sev => {
      const s = String(sev || '').toUpperCase();
      const cls =
        s === 'HIGH'
          ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
          : s === 'MEDIUM'
            ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200'
            : 'bg-slate-100 dark:bg-neutral-700 text-slate-700 dark:text-neutral-200';
      return (
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${cls}`}>
          {s || 'LOW'}
        </span>
      );
    };

    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-xs font-semibold text-slate-700 dark:text-neutral-200">{title}</div>
          <ConfidenceBadge value={confidence} />
        </div>
        <div className="space-y-2">
          {items.map((r, idx) => (
            <div
              key={`${r?.document || 'doc'}-${r?.category || 'cat'}-${idx}`}
              className="p-3 rounded-xl bg-white dark:bg-neutral-800 border border-slate-200 dark:border-neutral-700"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-xs font-semibold text-slate-800 dark:text-white truncate">
                    {r?.document} • {r?.category}
                  </div>
                  <div className="text-sm text-slate-700 dark:text-neutral-300 mt-1 whitespace-pre-wrap">
                    {r?.description}
                  </div>
                </div>
                <div className="flex-shrink-0">{severityBadge(r?.severity)}</div>
              </div>
              {r?.reasoning && (
                <div className="mt-2 text-xs text-slate-600 dark:text-neutral-400 whitespace-pre-wrap">
                  {r.reasoning}
                </div>
              )}
            </div>
          ))}
          {items.length === 0 && (
            <div className="text-sm text-slate-500 dark:text-neutral-400">
              No structured risk items returned.
            </div>
          )}
        </div>
      </div>
    );
  };

  const NumericTable = ({ data, confidence }) => {
    const items = Array.isArray(data?.items) ? data.items : [];
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-xs font-semibold text-slate-700 dark:text-neutral-200">
            Numeric extraction
          </div>
          <ConfidenceBadge value={confidence} />
        </div>
        <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-neutral-700 bg-white dark:bg-neutral-800">
          <table className="min-w-[720px] w-full text-xs">
            <thead className="bg-slate-50 dark:bg-neutral-900">
              <tr>
                <th className="text-left px-3 py-2 border-b border-slate-200 dark:border-neutral-700">
                  Document
                </th>
                <th className="text-left px-3 py-2 border-b border-slate-200 dark:border-neutral-700">
                  Item
                </th>
                <th className="text-left px-3 py-2 border-b border-slate-200 dark:border-neutral-700">
                  Value
                </th>
                <th className="text-left px-3 py-2 border-b border-slate-200 dark:border-neutral-700">
                  Notes
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <tr
                  key={`${it?.document || 'doc'}-${it?.item || 'item'}-${idx}`}
                  className="align-top"
                >
                  <td className="px-3 py-2 border-b border-slate-100 dark:border-neutral-700 text-slate-700 dark:text-neutral-300 whitespace-pre-wrap">
                    {it?.document}
                  </td>
                  <td className="px-3 py-2 border-b border-slate-100 dark:border-neutral-700 text-slate-700 dark:text-neutral-300 whitespace-pre-wrap">
                    {it?.item}
                  </td>
                  <td className="px-3 py-2 border-b border-slate-100 dark:border-neutral-700 text-slate-700 dark:text-neutral-300 whitespace-pre-wrap">
                    {it?.value}
                  </td>
                  <td className="px-3 py-2 border-b border-slate-100 dark:border-neutral-700 text-slate-600 dark:text-neutral-400 whitespace-pre-wrap">
                    {it?.notes || ''}
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td className="px-3 py-3 text-slate-500 dark:text-neutral-400" colSpan={4}>
                    No numeric items returned.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const SummaryBlock = ({ data, confidence }) => (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold text-slate-700 dark:text-neutral-200">Summary</div>
        <ConfidenceBadge value={confidence} />
      </div>
      <div className="p-3 rounded-xl bg-white dark:bg-neutral-800 border border-slate-200 dark:border-neutral-700">
        <div className="text-sm text-slate-700 dark:text-neutral-300 whitespace-pre-wrap">
          {data?.summary || 'Not specified'}
        </div>
        {Array.isArray(data?.keyPoints) && data.keyPoints.length > 0 && (
          <ul className="mt-3 list-disc pl-5 text-sm text-slate-700 dark:text-neutral-300 space-y-1">
            {data.keyPoints.map((p, i) => (
              <li key={i} className="whitespace-pre-wrap">
                {p}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );

  const markAssistantAnimationDone = (messageId) => {
    if (!messageId) return;
    setAnimatedAssistantIds(prev => {
      if (!prev.has(messageId)) return prev;
      const next = new Set(prev);
      next.delete(messageId);
      return next;
    });
  };

  const renderAssistantAnswer = (message, { animate = false } = {}) => {
    const meta = message?.metadata || {};
    const taskType = meta?.taskType;
    const structured = meta?.structured;
    const confidence = meta?.confidence || meta?.structured?.confidence || 'low';

    if (structured && taskType) {
      if (taskType === 'COMPARISON')
        return <ComparisonTable data={structured} confidence={confidence} />;
      if (taskType === 'RISKS')
        return <RisksList data={structured} confidence={confidence} title="Risks / red flags" />;
      if (taskType === 'LOOPHOLES')
        return <RisksList data={structured} confidence={confidence} title="Loopholes" />;
      if (taskType === 'NUMERIC_EXTRACTION')
        return <NumericTable data={structured} confidence={confidence} />;
      if (taskType === 'SUMMARY') return <SummaryBlock data={structured} confidence={confidence} />;
    }

    if (!animate) return renderMessageContent(message);

    return (
      <AnimatedResponse
        text={message?.content || ''}
        speed={42}
        isDark={isDarkMode}
        onComplete={() => markAssistantAnimationDone(message?.id)}
        renderText={(displayedText, isComplete) => {
          if (isComplete) return renderMessageContent(message);
          return (
            <div className="whitespace-pre-wrap leading-relaxed text-[#1a1a1a] dark:text-[#e5e5e5]">
              {displayedText}
              <span className="inline-block ml-0.5 animate-pulse text-[#E87A2A]">|</span>
            </div>
          );
        }}
      />
    );
  };

  const suggestedQuestions = [
    'What are the key themes across these documents?',
    'Summarize the main arguments presented',
    'What are potential risks or concerns mentioned?',
  ];

  const SystemMessage = ({ children }) => (
    <div className="flex justify-start">
      <div className="max-w-[80%] rounded-2xl px-4 py-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 text-sm text-red-700 dark:text-red-300">
        {children}
      </div>
    </div>
  );

  return (
    <div className="h-full flex flex-col">
      {/* Processing Mode Banner */}
      <ProcessingModeBanner notebook={notebook} />

      {notebook?.missingSourcesCount > 0 && (
        <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-900/50 text-xs text-amber-700 dark:text-amber-300">
          <AlertTriangle className="h-4 w-4" />
          Some sources failed to process. Answers may be incomplete.
        </div>
      )}

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          shouldShowProcessingSpinner ? (
            <ProcessingSpinner
              stage={currentStage}
              stageMessage={currentStageMessage}
              currentStep={currentStep}
              totalSteps={8}
              isDark={isDarkMode}
            />
          ) : (
            <div className="h-full flex flex-col items-center justify-center px-6">
              <ChatGreeting
                userName={userFirstName || user?.name || ''}
                isDark={isDarkMode}
                suggestedQuestions={hasSources ? suggestedQuestions : []}
                onQuestionClick={(q) => handleSend(q)}
              />
              {!hasSources && (
                <div className="mt-6 flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 rounded-xl">
                  <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                  <p className="text-xs text-amber-700 dark:text-amber-300">
                    Upload some sources to start asking questions
                  </p>
                </div>
              )}
              {hasSources && !hasReadySource && !isProcessing && (
                <div className="mt-6 flex items-center gap-2 px-4 py-2 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 rounded-xl">
                  <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />
                  <p className="text-xs text-red-700 dark:text-red-300">
                    Sources are not ready yet. Retry failed sources from the Sources panel.
                  </p>
                </div>
              )}
            </div>
          )
        ) : (
          <>
            {messages.map(message => {
              const shouldAnimateResponse =
                message.role === 'assistant' && animatedAssistantIds.has(message.id);
              return (
                <div
                  key={message.id}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      message.role === 'user'
                        ? 'bg-amber-500 text-white'
                        : 'bg-white dark:bg-neutral-800 border border-slate-200 dark:border-neutral-700'
                    }`}
                  >
                    {message.role === 'assistant' && (
                      <div className="flex items-center gap-2 mb-2 pb-2 border-b border-slate-100 dark:border-neutral-700">
                        <div className="p-1 bg-amber-100 dark:bg-amber-900/30 rounded">
                          <Sparkles className="h-3 w-3 text-amber-600 dark:text-amber-400" />
                        </div>
                        <span className="text-xs text-slate-500 dark:text-neutral-400">
                          Exploratory Response
                        </span>
                      </div>
                    )}

                    <div
                      className={
                        message.role === 'user'
                          ? 'text-sm'
                          : 'text-sm text-slate-700 dark:text-neutral-300'
                      }
                    >
                      {message.role === 'assistant'
                        ? renderAssistantAnswer(message, { animate: shouldAnimateResponse })
                        : message.content}
                    </div>

                    {/* Citations list */}
                    {message.role === 'assistant' && message.citations?.length > 0 && (
                      <div className="mt-3 pt-2 border-t border-slate-100 dark:border-neutral-700">
                        <p className="text-xs text-slate-400 dark:text-neutral-500 mb-1.5">
                          Sources:
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {(() => {
                            const seen = new Set();
                            const uniq = (message.citations || []).filter(c => {
                              const key = String(c?.sourceId || c?.fileName || '');
                              if (!key) return true;
                              if (seen.has(key)) return false;
                              seen.add(key);
                              return true;
                            });

                            return uniq.map((citation, i) => (
                              <button
                                key={`${citation?.sourceId || citation?.fileName || 'src'}-${i}`}
                                onClick={() => onCitationClick?.(citation)}
                                className="flex items-center gap-1 px-2 py-1 bg-slate-100 dark:bg-neutral-700 text-slate-600 dark:text-neutral-300 text-xs rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
                              >
                                <BookOpen className="h-3 w-3" />
                                {citation.fileName || citation.sourceName || `Source ${i + 1}`}
                                <ExternalLink className="h-3 w-3 ml-0.5" />
                              </button>
                            ));
                          })()}
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    {message.role === 'assistant' && (
                      <div className="flex items-center gap-2 mt-2 pt-2">
                        <button
                          onClick={() => handleCopy(message.content, message.id)}
                          className="p-1.5 hover:bg-slate-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                          title="Copy"
                        >
                          {copiedId === message.id ? (
                            <Check className="h-3.5 w-3.5 text-green-500" />
                          ) : (
                            <Copy className="h-3.5 w-3.5 text-slate-400" />
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Loading indicator */}
            {isLoading && (
              <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 rounded-xl">
                <Loader2 className="h-4 w-4 text-amber-500 animate-spin" />
                <span className="text-sm text-slate-500 dark:text-neutral-400">Thinking...</span>
              </div>
            )}

            {/* Error (non-silent, traceable, preserves chat continuity) */}
            {error && (
              <SystemMessage>
                ⚠️ {typeof error === 'string' ? error : 'Chat request failed'}
                <br />
                Trace ID: {errorTraceId || 'n/a'}
              </SystemMessage>
            )}

            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input Area */}
      <div className="flex-shrink-0 p-4 border-t border-slate-200 dark:border-neutral-700 bg-white dark:bg-neutral-900">
        <div className="flex items-center gap-2">
          {messages.length > 0 && canClearChat && (
            <button
              onClick={handleClearHistory}
              className="p-2 hover:bg-slate-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
              title="Clear chat"
            >
              <Trash2 className="h-5 w-5 text-slate-400" />
            </button>
          )}

          <div className="flex-1 relative">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
              placeholder="Ask a question about your sources..."
              disabled={isLoading || !notebook?.sources?.length}
              className="w-full px-4 py-3 bg-slate-100 dark:bg-neutral-800 border-0 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 pr-12 disabled:opacity-50"
            />
            <button
              onClick={handleSend}
              disabled={isLoading || !input.trim() || !notebook?.sources?.length}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>

        {/* Exploratory reminder */}
        <p className="text-[10px] text-slate-400 dark:text-neutral-500 text-center mt-2">
          ⚡ Exploratory output — verify important information
        </p>
      </div>
    </div>
  );
};

export default ChatPanel;
